import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';

import { HttpStatusCode, UserSettings } from '@models';
import { RoutesService, SettingsService, UserSessionService } from '@services';
import { isHttpUrl, isSameOrigin } from '@shared/utils';
import { AppState, resetUser, setIsLoggedIn, setSessionToken } from '@store';

export const STRHTTPInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const store = inject(Store<AppState>);
  const cookieService = inject(CookieService);
  const routesService = inject(RoutesService);
  const settingsService = inject(SettingsService);
  const userSessionService = inject(UserSessionService);

  return store
    .select((state) => state.user)
    .pipe(
      take(1),
      mergeMap((userBean) =>
        store
          .select((state) => state.auth.sessionToken)
          .pipe(
            take(1),
            mergeMap((sessionToken) => {
              const pathsNoAuth = ['/settings', '/locale'];

              const locale = cookieService.get('locale') || 'en_US';
              let request = req.clone({
                setHeaders: {
                  'Accept-Language': locale,
                },
              });

              if (
                sessionToken &&
                isSameOrigin(request.url) &&
                !pathsNoAuth.some((criterion) =>
                  request.url.includes(criterion)
                )
              ) {
                request = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${sessionToken}`,
                  },
                });
              }

              return next(request).pipe(
                tap((event: HttpEvent<unknown>) => {
                  if (event instanceof HttpResponse) {
                    if (event.url?.includes('agora-api/api/v1/logout')) {
                      router.navigate([], { queryParams: {} });
                    }

                    if (
                      userBean &&
                      event.url?.includes('agora-api/api/v1/') &&
                      !event.url.includes('logout')
                    ) {
                      userSessionService.startTimer();
                    }
                  }
                }),
                catchError((error: HttpErrorResponse) => {
                  return handleError(
                    error,
                    store,
                    settingsService,
                    cookieService,
                    userSessionService,
                    routesService,
                    router,
                    userBean
                  );
                })
              );
            })
          )
      )
    );
};

const navigateToServiceUnavailableAfterFallbackRedirects = (router: Router) => {
  setTimeout(() => {
    router.navigate(['/503']);
  });
};

function handleError(
  error: HttpErrorResponse,
  store: Store<AppState>,
  settingsService: SettingsService,
  cookieService: CookieService,
  userSessionService: UserSessionService,
  routesService: RoutesService,
  router: Router,
  userBean: Nullable<UserSettings>
): Observable<never> {
  if (error.status === HttpStatusCode.NOT_FOUND && userBean?.userId) {
    routesService.resetPreviousRoute();
    userSessionService.startTimer();
  }

  if (error.status === HttpStatusCode.UNAUTHORIZED) {
    if (routesService.getLoggedInUserId()) {
      routesService.saveCurrentUrlForSessionRestore();
    }

    userSessionService.stopTimer();
    store.dispatch(resetUser());
    store.dispatch(setSessionToken({ payload: null }));
    store.dispatch(setIsLoggedIn({ payload: false }));
    settingsService.resetRootData();
    cookieService.delete('adminSessionToken');

    if (!routesService.getIsPublicPath(window.location.pathname)) {
      if (isHttpUrl(error.error?.web_url)) {
        window.location.href = error.error.web_url;
      } else {
        const incoming = new URLSearchParams(window.location.search);
        const queryParams: Record<string, string> = {};

        for (const key of ['username', 'community', 'authKey']) {
          const value = incoming.get(key);

          if (value) {
            queryParams[key] = value;
          }
        }

        router.navigate(['/login'], { queryParams });
      }
    }
  } else if (error.status === HttpStatusCode.SERVICE_UNAVAILABLE) {
    userSessionService.stopTimer();
    store.dispatch(resetUser());
    store.dispatch(setSessionToken({ payload: null }));
    store.dispatch(setIsLoggedIn({ payload: false }));
    settingsService.resetRootData();
    cookieService.delete('adminSessionToken');
    navigateToServiceUnavailableAfterFallbackRedirects(router);
  }

  return throwError(() => error);
}
